// src/components/SubmissionDetails.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "./SubmissionDetails.css";
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Divider,
} from "@mui/material";
import {
  FaArrowLeft,
  FaPrint,
  FaCheckCircle,
  FaTimesCircle,
  FaTrash as FaTrashIcon,
  FaEdit,
  FaSave,
  FaTimes,
  FaPlus,
} from "react-icons/fa";

import api, { getApiBase } from "../api";

const DEFAULT_IMG = "/no-image.png";

function makePhotoBase() {
  const envOverride = process.env.REACT_APP_PHOTO_BASE_URL;
  if (envOverride) return envOverride.replace(/\/+$/g, "");
  const origin = (getApiBase() || "").replace(/\/api\/?$/i, "");
  return `${origin}/storage/products`;
}
const PHOTO_BASE_URL = makePhotoBase();

function errToText(err) {
  try {
    const r = err?.response;
    if (!r) return err?.message || "Gagal melakukan permintaan.";
    const d = r.data;
    if (typeof d === "string") return d;
    if (typeof d?.message === "string" && d.message.trim()) return d.message;
    if (d?.error) return String(d.error);
    if (d?.detail) return String(d.detail);
    if (d?.errors && typeof d.errors === "object") {
      const msgs = [];
      Object.values(d.errors).forEach((v) => {
        if (Array.isArray(v)) v.forEach((t) => t && msgs.push(String(t)));
        else if (v) msgs.push(String(v));
      });
      if (msgs.length) return msgs.join("\n");
    }
    return `Gagal (${r.status})`;
  } catch {
    return "Terjadi kesalahan yang tidak diketahui.";
  }
}

function toUiStatus(raw) {
  const s = String(raw || "").toLowerCase().trim();
  if (["submitted", "pending", "menunggu"].includes(s)) return "Pending";
  if (["approved", "disetujui"].includes(s)) return "Approved";
  if (["shipped", "dikirim", "sedang dikirim"].includes(s)) return "Shipped";
  if (["delivered", "diterima", "telah diterima", "selesai"].includes(s)) return "Delivered";
  if (["rejected", "ditolak"].includes(s)) return "Rejected";
  return "Unknown";
}

function getStatusColor(uiStatus) {
  switch (uiStatus) {
    case "Pending":
      return "warning";
    case "Approved":
      return "info";
    case "Shipped":
      return "primary";
    case "Delivered":
      return "success";
    case "Rejected":
      return "error";
    default:
      return "default";
  }
}

function getStatusLabel(uiStatus) {
  switch (uiStatus) {
    case "Pending":
      return "Menunggu";
    case "Approved":
      return "Disetujui";
    case "Shipped":
      return "Dalam pengiriman";
    case "Delivered":
      return "Telah diterima";
    case "Rejected":
      return "Ditolak";
    default:
      return "Tidak diketahui";
  }
}

function fmtDate(v) {
  if (!v) return "-";
  try {
    return new Date(v).toLocaleDateString("id-ID");
  } catch {
    return String(v);
  }
}

function sourceLabel(src) {
  const s = String(src || "").toLowerCase();
  if (s === "pembelian") return "Pembelian";
  if (s === "transfer_masuk") return "Transfer Masuk";
  return src || "-";
}

// ✅ untuk HTML print agar aman
function escapeHtml(str = "") {
  return String(str)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function SubmissionDetails(props) {
  const navigate = useNavigate();
  const params = useParams();

  const submissionProp = props.submission || props.submissionDetails || null;
  const userRole = props.userRole || "regular";
  const onBack = props.onBack || props.onBackToDashboard || (() => navigate(-1));
  const onGoToShipping =
    props.onGoToShipping ||
    (props.setCurrentView ? () => props.setCurrentView("pengiriman") : null);

  const number = params.number || submissionProp?.number || submissionProp?.id || null;

  const [detail, setDetail] = useState(null);
  const [currentItems, setCurrentItems] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [busyAction, setBusyAction] = useState(false);

  const [approveLoadingBatches, setApproveLoadingBatches] = useState(false);
  const [approveBatchErr, setApproveBatchErr] = useState("");
  const [batchOptionsByPid, setBatchOptionsByPid] = useState({});
  const [batchQtyByPid, setBatchQtyByPid] = useState({});
  const [approveValidateErr, setApproveValidateErr] = useState("");

  const [availableProducts, setAvailableProducts] = useState([]);
  const [newProductId, setNewProductId] = useState("");
  const [newQuantityToAdd, setNewQuantityToAdd] = useState(1);

  // ✅ dialog pilih jenis cetak
  const [printOpen, setPrintOpen] = useState(false);

  const uiStatus = useMemo(() => {
    const raw = detail?.status ?? submissionProp?.status ?? "";
    return toUiStatus(raw);
  }, [detail, submissionProp]);

  const canEdit = uiStatus === "Pending";

  const itemsForView = useMemo(() => {
    if (detail?.items && Array.isArray(detail.items)) return detail.items;
    if (Array.isArray(currentItems)) return currentItems;
    return [];
  }, [detail, currentItems]);

  // ✅ SEKARANG: semua item qty>0 wajib alokasi batch
  const wajibBatchItems = useMemo(() => {
    return (itemsForView || []).filter((it) => (parseInt(it?.qty, 10) || 0) > 0);
  }, [itemsForView]);

  const getStokSisa = (pid, incomingId) => {
    const opts = batchOptionsByPid?.[Number(pid)] || [];
    const row = opts.find((r) => Number(r.id) === Number(incomingId));
    const sisa = row?.stok_sisa ?? row?.stokSisa ?? row?.stok_sisa_batch;
    return Number.isFinite(Number(sisa)) ? Number(sisa) : 0;
  };

  useEffect(() => {
    if (!number) return;
    const load = async () => {
      try {
        const res = await api.get(`/submissions/${encodeURIComponent(number)}`);
        const data = res.data?.data || {};
        const mappedItems = (data.items || []).map((it) => ({
          ...it,
          image: it.photo ? `${PHOTO_BASE_URL}/${it.photo}` : undefined,

          // ✅ TANPA unit/satuan
          sisa_stok: it.sisa_stok ?? it.sisaStok ?? it.sisa_stok_timker ?? null,
          keperluan: it.keperluan ?? it.keterangan ?? "",
        }));
        setDetail({ ...data, items: mappedItems });
        setCurrentItems(mappedItems);
        setIsEditing(false);
        setNewProductId("");
        setNewQuantityToAdd(1);
      } catch {
        setDetail(null);
      }
    };
    load();
  }, [number]);

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const res = await api.get("/products");
        const list = Array.isArray(res.data) ? res.data : Array.isArray(res.data?.data) ? res.data.data : [];
        const mapped = list.map((p) => ({
          id: p.id,
          name: p.name,
          category: p.category?.name || "Tidak Diketahui",
          stock: p.stock,
          image: p.photo ? `${PHOTO_BASE_URL}/${p.photo}` : undefined,
        }));
        setAvailableProducts(mapped);
      } catch {
        setAvailableProducts([]);
      }
    };
    loadProducts();
  }, []);

  const handleQuantityChange = (index, newQty) => {
    const updated = [...currentItems];
    const n = Number(newQty);
    if (newQty === "") {
      updated[index] = { ...updated[index], qty: "" };
    } else if (Number.isInteger(n) && n >= 0) {
      updated[index] = { ...updated[index], qty: n };
    }
    setCurrentItems(updated);
  };

  const handleDecrement = (index) => {
    const updated = [...currentItems];
    const cur = parseInt(updated[index].qty, 10) || 0;
    updated[index] = { ...updated[index], qty: Math.max(0, cur - 1) };
    setCurrentItems(updated);
  };

  const handleIncrement = (index) => {
    const updated = [...currentItems];
    const cur = parseInt(updated[index].qty, 10) || 0;
    updated[index] = { ...updated[index], qty: cur + 1 };
    setCurrentItems(updated);
  };

  const handleRemoveItem = (index) => {
    if (!window.confirm("Hapus item ini?")) return;
    setCurrentItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleNewProductChange = (e) => {
    const val = e.target.value;
    setNewProductId(val === "" ? "" : Number(val));
  };

  const handleNewQuantityChange = (e) => {
    const q = parseInt(e.target.value, 10);
    setNewQuantityToAdd(!isNaN(q) && q > 0 ? q : 1);
  };

  const handleAddItem = () => {
    if (newProductId === "" || newQuantityToAdd < 1) return;
    const product = availableProducts.find((p) => Number(p.id) === Number(newProductId));
    if (!product) return;

    setCurrentItems((prev) => {
      const idx = prev.findIndex((it) => Number(it.id) === Number(product.id));
      if (idx > -1) {
        const cur = parseInt(prev[idx].qty, 10) || 0;
        const copy = [...prev];
        copy[idx] = { ...copy[idx], qty: cur + newQuantityToAdd };
        return copy;
      }
      return [
        ...prev,
        {
          id: product.id,
          name: product.name,
          category: product.category,
          qty: newQuantityToAdd,
          image: product.image,
          stock: product.stock,

          // ✅ TANPA unit/satuan
          sisa_stok: null,
          keperluan: "",
        },
      ];
    });

    setNewProductId("");
    setNewQuantityToAdd(1);
  };

  const handleSaveChanges = async () => {
    if (!number) return;

    try {
      const latestRes = await api.get(`/submissions/${encodeURIComponent(number)}`);
      const latest = latestRes.data?.data || {};
      const latestStatus = String(latest.status || "").toLowerCase();
      if (latestStatus !== "submitted") {
        const mapped = (latest.items || []).map((it) => ({
          ...it,
          image: it.photo ? `${PHOTO_BASE_URL}/${it.photo}` : undefined,
        }));
        setDetail({ ...latest, items: mapped });
        setCurrentItems(mapped);
        setIsEditing(false);
        setApproveOpen(false);
        alert("Tidak dapat mengubah karena status bukan Menunggu.");
        return;
      }
    } catch {
      alert("Gagal memeriksa status terbaru. Muat ulang halaman lalu coba lagi.");
      return;
    }

    const itemsToSave = currentItems
      .map((it) => ({ product_id: it.id, qty: parseInt(it.qty, 10) || 0 }))
      .filter((r) => r.qty > 0);

    if (itemsToSave.length === 0) {
      if (!window.confirm("Semua jumlah 0. Ini akan mengosongkan pengajuan. Lanjutkan?")) return;
    }

    try {
      setSaving(true);
      await api.patch(`/submissions/${encodeURIComponent(number)}`, { items: itemsToSave });

      const res = await api.get(`/submissions/${encodeURIComponent(number)}`);
      const data = res.data?.data || {};
      const mappedItems = (data.items || []).map((it) => ({
        ...it,
        image: it.photo ? `${PHOTO_BASE_URL}/${it.photo}` : undefined,
      }));
      setDetail({ ...data, items: mappedItems });
      setCurrentItems(mappedItems);
      setIsEditing(false);
      alert("Perubahan pengajuan berhasil disimpan.");
    } catch (e) {
      alert(errToText(e));
    } finally {
      setSaving(false);
    }
  };

  const openApproveModal = async () => {
    setApproveOpen(true);
    setApproveValidateErr("");
    setApproveBatchErr("");
    setBatchOptionsByPid({});
    setBatchQtyByPid({});

    if (!wajibBatchItems.length) return;

    try {
      setApproveLoadingBatches(true);

      const newOptions = {};
      const newQtyMap = {};

      for (const it of wajibBatchItems) {
        const pid = Number(it.id);

        const res = await api.get("/incoming_products/available", {
          params: { product_id: pid },
        });

        const list = Array.isArray(res?.data?.data) ? res.data.data : [];

        const sorted = [...list].sort((a, b) => {
          const ak = a?.tanggal_kadaluarsa ? new Date(a.tanggal_kadaluarsa).getTime() : Number.MAX_SAFE_INTEGER;
          const bk = b?.tanggal_kadaluarsa ? new Date(b.tanggal_kadaluarsa).getTime() : Number.MAX_SAFE_INTEGER;
          if (ak !== bk) return ak - bk;
          return (a?.id || 0) - (b?.id || 0);
        });

        newOptions[pid] = sorted;

        const perIncoming = {};
        sorted.forEach((row) => {
          if (row?.id) perIncoming[Number(row.id)] = 0;
        });
        newQtyMap[pid] = perIncoming;
      }

      setBatchOptionsByPid(newOptions);
      setBatchQtyByPid(newQtyMap);
    } catch (e) {
      setApproveBatchErr(errToText(e));
    } finally {
      setApproveLoadingBatches(false);
    }
  };

  const openRejectModal = () => setRejectOpen(true);

  const setBatchQty = (productId, incomingId, value) => {
    const pid = Number(productId);
    const iid = Number(incomingId);

    setBatchQtyByPid((prev) => {
      const copy = { ...prev };
      const perPid = { ...(copy[pid] || {}) };

      if (value === "") {
        perPid[iid] = "";
        copy[pid] = perPid;
        return copy;
      }

      let n = parseInt(value, 10);
      if (isNaN(n) || n < 0) n = 0;

      const max = getStokSisa(pid, iid);
      if (n > max) n = max;

      perPid[iid] = n;
      copy[pid] = perPid;
      return copy;
    });
  };

  const sumAllocatedForPid = (pid) => {
    const perPid = batchQtyByPid?.[Number(pid)] || {};
    return Object.values(perPid).reduce((acc, v) => acc + (parseInt(v, 10) || 0), 0);
  };

  const validateAllocations = (latestItems) => {
    const reqItems = (latestItems || []).filter((it) => (parseInt(it?.qty, 10) || 0) > 0);
    if (!reqItems.length) return { ok: false, message: "Item pengajuan kosong." };

    for (const it of reqItems) {
      const pid = Number(it.id);
      const opts = batchOptionsByPid?.[pid] || [];
      if (!opts.length) return { ok: false, message: `Tidak ada batch tersedia (stok sisa) untuk '${it.name}'.` };
    }

    const payload = [];

    for (const it of reqItems) {
      const pid = Number(it.id);
      const need = parseInt(it.qty, 10) || 0;
      const perPid = batchQtyByPid?.[pid] || {};

      for (const [incomingId, qty] of Object.entries(perPid)) {
        const q = parseInt(qty, 10) || 0;
        if (q <= 0) continue;

        const max = getStokSisa(pid, incomingId);
        if (q > max) {
          return {
            ok: false,
            message: `Qty keluar melebihi stok sisa batch. (Produk: '${it.name}', incoming_id=${incomingId}, sisa=${max}, diisi=${q})`,
          };
        }
      }

      const allocated = Object.values(perPid).reduce((acc, v) => acc + (parseInt(v, 10) || 0), 0);
      if (allocated !== need) {
        return {
          ok: false,
          message: `Total qty batch untuk '${it.name}' harus sama dengan jumlah pengajuan. (Butuh ${need}, dipilih ${allocated})`,
        };
      }

      const any = Object.values(perPid).some((v) => (parseInt(v, 10) || 0) > 0);
      if (!any && need > 0) return { ok: false, message: `Pilih batch untuk '${it.name}'.` };

      Object.entries(perPid).forEach(([incomingId, qty]) => {
        const q = parseInt(qty, 10) || 0;
        if (q > 0) {
          payload.push({
            product_id: pid,
            product_incoming_id: Number(incomingId),
            qty: q,
          });
        }
      });
    }

    if (!payload.length) return { ok: false, message: "Batch allocation kosong." };
    return { ok: true, payload };
  };

  const doApprove = async () => {
    if (!number) return;

    try {
      setBusyAction(true);
      setApproveValidateErr("");

      const latestRes = await api.get(`/submissions/${encodeURIComponent(number)}`);
      const latest = latestRes.data?.data || {};
      const latestStatus = String(latest.status || "").toLowerCase();
      const latestItems = Array.isArray(latest.items) ? latest.items : [];

      if (latestStatus !== "submitted") {
        alert("Status pengajuan bukan 'Menunggu'. Tidak dapat disetujui.");
        setBusyAction(false);
        return;
      }
      if (latestItems.length === 0) {
        alert("Item pengajuan kosong. Tidak dapat disetujui.");
        setBusyAction(false);
        return;
      }

      const v = validateAllocations(latestItems);
      if (!v.ok) {
        setApproveValidateErr(v.message || "Batch allocation tidak valid.");
        setBusyAction(false);
        return;
      }

      await api.post(`/submissions/${encodeURIComponent(number)}/approve`, {
        batch_allocations: v.payload,
      });

      setApproveOpen(false);
      alert("Pengajuan disetujui.");
      if (typeof onGoToShipping === "function") onGoToShipping();
      else navigate("/admin/pengiriman");
    } catch (e) {
      alert(errToText(e));
    } finally {
      setBusyAction(false);
    }
  };

  const doReject = async () => {
    if (!number) return;
    try {
      setBusyAction(true);

      const latestRes = await api.get(`/submissions/${encodeURIComponent(number)}`);
      const latest = latestRes.data?.data || {};
      const latestStatus = String(latest.status || "").toLowerCase();
      if (latestStatus !== "submitted") {
        alert("Status pengajuan bukan 'Menunggu'. Tidak dapat ditolak.");
        setBusyAction(false);
        return;
      }

      await api.post(`/submissions/${encodeURIComponent(number)}/reject`, {});
      setRejectOpen(false);
      alert("Pengajuan ditolak.");
      onBack();
    } catch (e) {
      alert(errToText(e));
    } finally {
      setBusyAction(false);
    }
  };

  const removeSubmission = async () => {
    if (!number) return;
    if (!window.confirm(`Hapus pengajuan ${number}?`)) return;
    try {
      await api.delete(`/submissions/${encodeURIComponent(number)}`);
      alert("Pengajuan dihapus.");
      onBack();
    } catch (e) {
      alert(errToText(e));
    }
  };

  // =========================
  // CETAK SBBK (tanpa satuan)
  // =========================
  const handlePrintSbbk = () => {
    const data = detail || submissionProp;
    if (!data) {
      window.print();
      return;
    }

    const tanggal = data.date || data.submission_date || "";
    const d = tanggal ? new Date(tanggal) : new Date();
    const day = String(d.getDate()).padStart(2, "0");
    const monthIdx = d.getMonth();
    const year = d.getFullYear();
    const MONTHS_ID = ["JANUARI","FEBRUARI","MARET","APRIL","MEI","JUNI","JULI","AGUSTUS","SEPTEMBER","OKTOBER","NOVEMBER","DESEMBER"];
    const monthName = MONTHS_ID[monthIdx] || "";
    const todayStr = `${day} ${monthName} ${year}`;

    const workRaw = (detail && detail.work_area) || (submissionProp && submissionProp.work_area) || "";
    let workAreaPhrase = "";
    if (workRaw) {
      const upper = String(workRaw).toUpperCase().trim();
      if (upper.includes("WILKER") || upper.includes("TIMKER")) workAreaPhrase = upper;
      else workAreaPhrase = `WILKER ${upper}`;
    } else {
      workAreaPhrase = "........................................................";
    }

    const itemsSource =
      (detail && Array.isArray(detail.items) && detail.items.length ? detail.items : currentItems) || [];

    const maxRows = 12;
    const rowsHtml = [];
    for (let i = 0; i < maxRows; i++) {
      const idx = i + 1;
      const it = itemsSource[i];
      const name = it ? it.name || "" : "";
      const qty = it ? it.qty || it.quantity || "" : "";
      const qtyText = `${qty}`;

      rowsHtml.push(`
      <tr>
        <td style="border:1px solid #000; padding:2px 4px; text-align:center;">${idx}</td>
        <td style="border:1px solid #000; padding:2px 4px;">${name}</td>
        <td style="border:1px solid #000; padding:2px 4px; text-align:center;">${qtyText}</td>
        <td style="border:1px solid #000; padding:2px 4px;"></td>
      </tr>
    `);
    }

    const nomorSbbk = data.number || number || "";
    const origin = window.location.origin || "";

    const html = `
<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>SBBK ${nomorSbbk}</title>
<style>
  * { box-sizing: border-box; }
  body { margin: 0; font-family: "Times New Roman", serif; font-size: 11pt; }
  .page { width: 210mm; min-height: 297mm; margin: 10mm auto; padding: 10mm 15mm 15mm 15mm; }
  .kop-wrapper { text-align: center; margin-bottom: 6mm; }
  .kop-wrapper img { width: 100%; height: auto; max-height: 95px; }
  h1 { font-size: 13pt; text-align: center; margin: 0; font-weight: bold; }
  .nomor { text-align: center; font-size: 10pt; margin-top: 2px; margin-bottom: 4mm; }
  .paragraf { font-size: 10pt; text-align: left; margin-bottom: 3mm; }
  table { border-collapse: collapse; width: 100%; font-size: 10pt; }
  thead td { font-weight: bold; text-align: center; }
  .ttd-wrapper { width: 100%; margin-top: 18mm; font-size: 10pt; }
  @page { size: A4; margin: 10mm 15mm 15mm 15mm; }
</style>
</head>
<body>
  <div class="page">
    <div class="kop-wrapper">
      <img src="${origin}/kop.png" alt="Kop SBBK" />
    </div>

    <h1>SURAT BUKTI BARANG KELUAR (SBBK)</h1>
    <div class="nomor">Nomor : ${nomorSbbk}</div>

    <div class="paragraf">
      Pada hari ini tanggal ${day}, bulan ${monthName} Tahun ${year}, telah dikeluarkan barang persediaan berupa barang habis pakai untuk dipergunakan pada <strong>${workAreaPhrase}</strong>. Adapun rincian barang sebagai berikut:
    </div>

    <table>
      <thead>
        <tr>
          <td style="border:1px solid #000; width:6%;">NO</td>
          <td style="border:1px solid #000;">NAMA BARANG</td>
          <td style="border:1px solid #000; width:12%;">JUMLAH</td>
          <td style="border:1px solid #000; width:25%;">KETERANGAN</td>
        </tr>
      </thead>
      <tbody>${rowsHtml.join("")}</tbody>
    </table>

    <table class="ttd-wrapper">
      <tr>
        <td style="width:50%; vertical-align:top;">
          Yang Menerima,<br/><br/><br/><br/><br/><br/><br/><br/>
          (.............................................)<br/>
          NIP. .............................
        </td>
        <td style="width:50%; vertical-align:top; text-align:center;">
          Pangkalpinang, ${todayStr}<br/>
          Yang Menyerahkan,<br/><br/>
          <img src="${origin}/ttd-devi.png" alt="Tanda tangan" style="height:60px;"/><br/>
          <strong>Devi Andriani Purnamasari</strong><br/>
          NIP: 199002052022032001
        </td>
      </tr>
    </table>
  </div>
</body>
</html>
`;

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow || iframe.contentDocument?.defaultView || iframe.contentDocument;
    const docEl = doc.document || doc;

    docEl.open();
    docEl.write(html);
    docEl.close();

    setTimeout(() => {
      doc.focus();
      doc.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 500);
  };

  // ============================================
  // CETAK FORM PENGAJUAN (tanpa satuan)
  // ============================================
  const handlePrintFormPengajuan = () => {
    const data = detail || submissionProp;
    if (!data) {
      window.print();
      return;
    }

    const origin = window.location.origin || "";
    const tanggal = data.date || data.submission_date || "";
    const statusLabel = getStatusLabel(uiStatus);

    const diajukanOleh =
      detail?.work_area ||
      submissionProp?.work_area ||
      detail?.user_name ||
      submissionProp?.user_name ||
      "-";

    const itemsSource =
      (detail && Array.isArray(detail.items) && detail.items.length ? detail.items : currentItems) || [];

    const rowsHtml = (itemsSource.length ? itemsSource : [])
      .map((it, idx) => {
        const name = escapeHtml(it?.name || "-");
        const category = escapeHtml(it?.category || "Tidak Diketahui");
        const qty = escapeHtml(it?.qty ?? 0);
        const sisa = escapeHtml(it?.sisa_stok ?? it?.sisaStok ?? it?.sisa_stok_timker ?? "-");
        const kep = escapeHtml(it?.keperluan ?? it?.keterangan ?? "-");

        const imgSrc = it?.image ? it.image : `${origin}${DEFAULT_IMG}`;

        return `
      <tr>
        <td style="text-align:center;">${idx + 1}</td>
        <td>
          <div style="display:flex; gap:10px; align-items:center;">
            <img src="${imgSrc}" onerror="this.onerror=null;this.src='${origin}${DEFAULT_IMG}'"
                 style="width:40px;height:40px;object-fit:cover;border-radius:4px;border:1px solid #e5e7eb;" />
            <div>${name}</div>
          </div>
        </td>
        <td>${category}</td>
        <td style="text-align:center;">${qty}</td>
        <td style="text-align:center;">${sisa}</td>
        <td style="white-space:pre-wrap;">${kep}</td>
      </tr>
    `;
      })
      .join("");

    const html = `
<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>Form Detail Pengajuan/Permintaan - ${escapeHtml(data.number || number || "")}</title>
<style>
  * { box-sizing: border-box; }
  body { margin: 0; font-family: Arial, sans-serif; color:#111827; }
  .page { width: 210mm; min-height: 297mm; margin: 10mm auto; padding: 10mm 12mm; }
  h1 { font-size: 16pt; margin: 0 0 10px; text-align:center; }
  .meta { font-size: 11pt; margin-bottom: 10px; }
  .meta-row { margin: 4px 0; }
  .meta-row b { display:inline-block; min-width: 140px; }
  table { width: 100%; border-collapse: collapse; font-size: 10.5pt; }
  th, td { border: 1px solid #d1d5db; padding: 8px; vertical-align: top; }
  th { background: #f3f4f6; text-align: left; }
  @page { size: A4; margin: 10mm; }
</style>
</head>
<body>
  <div class="page">
    <h1>Form Detail Pengajuan/Permintaan</h1>

    <div class="meta">
      <div class="meta-row"><b>No. Pengajuan</b>: ${escapeHtml(data.number || number || "-")}</div>
      <div class="meta-row"><b>Tanggal Pengajuan</b>: ${escapeHtml(tanggal || "-")}</div>
      <div class="meta-row"><b>Status</b>: ${escapeHtml(statusLabel || "-")}</div>
      <div class="meta-row"><b>Diajukan Oleh</b>: ${escapeHtml(diajukanOleh || "-")}</div>
    </div>

    <table>
      <thead>
        <tr>
          <th style="width:40px;">No</th>
          <th style="width:320px;">Produk</th>
          <th style="width:190px;">Kategori</th>
          <th style="width:70px; text-align:center;">Jumlah</th>
          <th style="width:150px; text-align:center;">Sisa Stok (Timker/Wilker)</th>
          <th>Keperluan</th>
        </tr>
      </thead>
      <tbody>
        ${rowsHtml || `<tr><td colspan="6" style="text-align:center;">Tidak ada item.</td></tr>`}
      </tbody>
    </table>
  </div>
</body>
</html>
`;

    const iframe = document.createElement("iframe");
    iframe.style.position = "fixed";
    iframe.style.right = "0";
    iframe.style.bottom = "0";
    iframe.style.width = "0";
    iframe.style.height = "0";
    iframe.style.border = "0";
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow || iframe.contentDocument?.defaultView || iframe.contentDocument;
    const docEl = doc.document || doc;

    docEl.open();
    docEl.write(html);
    docEl.close();

    setTimeout(() => {
      doc.focus();
      doc.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    }, 500);
  };

  if (!number) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h6" color="textSecondary" align="center">
          Pengajuan tidak ditemukan.
        </Typography>
      </Container>
    );
  }

  // ✅ jumlah kolom tabel utama TANPA satuan
  const BASE_COLS = 5; // Produk, Kategori, Jumlah, Sisa Stok, Keperluan
  const COLS = isEditing ? BASE_COLS + 1 : BASE_COLS;

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 2 }}>
        <Button variant="outlined" startIcon={<FaArrowLeft />} onClick={onBack}>
          Kembali
        </Button>
      </Box>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Detail Pengajuan: {number}
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" sx={{ mb: 1 }}>
            <strong>Tanggal Pengajuan:</strong> {detail?.date || submissionProp?.date || "N/A"}
          </Typography>

          <Box sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body1" component="span">
              <strong>Status:</strong>
            </Typography>
            <Chip component="span" label={getStatusLabel(uiStatus)} color={getStatusColor(uiStatus)} size="small" />
          </Box>
        </Box>

        <Typography variant="h6" gutterBottom>
          Daftar Barang:
        </Typography>

        <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #ddd" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell sx={{ width: "30%" }}>PRODUK</TableCell>
                <TableCell sx={{ width: "18%" }}>KATEGORI</TableCell>
                <TableCell sx={{ width: "10%", textAlign: "center" }}>JUMLAH</TableCell>

                {/* ✅ SATUAN DIHAPUS */}
                <TableCell sx={{ width: "16%", textAlign: "center" }}>
                  SISA STOK
                  <br />
                  (Timker/Wilker)
                </TableCell>
                <TableCell sx={{ width: "26%" }}>KEPERLUAN</TableCell>

                {isEditing && <TableCell sx={{ width: "10%", textAlign: "center" }}>Aksi</TableCell>}
              </TableRow>
            </TableHead>

            <TableBody>
              {(currentItems || []).length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLS} sx={{ textAlign: "center" }}>
                    Tidak ada item.
                  </TableCell>
                </TableRow>
              ) : (
                currentItems.map((item, idx) => {
                  const sisa = item.sisa_stok ?? item.sisaStok ?? item.sisa_stok_timker ?? "-";
                  const kep = item.keperluan ?? item.keterangan ?? "-";

                  return (
                    <TableRow key={item.id || idx}>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <img
                            src={item.image || DEFAULT_IMG}
                            alt={item.name}
                            width={40}
                            height={40}
                            style={{ objectFit: "cover", borderRadius: 4 }}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = DEFAULT_IMG;
                            }}
                          />
                          <Typography variant="body2">{item.name || "Produk"}</Typography>
                        </Box>
                      </TableCell>

                      <TableCell>{item.category || "Tidak Diketahui"}</TableCell>

                      <TableCell sx={{ textAlign: "center" }}>
                        {isEditing ? (
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleDecrement(idx)}
                              disabled={parseInt(item.qty, 10) <= 0 || item.qty === ""}
                            >
                              -
                            </Button>
                            <TextField
                              type="number"
                              value={item.qty === "" ? "" : item.qty}
                              onChange={(e) => handleQuantityChange(idx, e.target.value)}
                              inputProps={{ min: 0, style: { textAlign: "center" } }}
                              size="small"
                              sx={{ width: 56 }}
                            />
                            <Button variant="outlined" size="small" onClick={() => handleIncrement(idx)}>
                              +
                            </Button>
                          </Box>
                        ) : (
                          item.qty ?? 0
                        )}
                      </TableCell>

                      {/* ✅ SATUAN DIHAPUS */}
                      <TableCell sx={{ textAlign: "center" }}>{sisa ?? "-"}</TableCell>
                      <TableCell sx={{ whiteSpace: "pre-wrap" }}>{kep || "-"}</TableCell>

                      {isEditing && (
                        <TableCell sx={{ textAlign: "center" }}>
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            startIcon={<FaTrashIcon />}
                            onClick={() => handleRemoveItem(idx)}
                          >
                            Hapus
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {isEditing && (
          <Box sx={{ mt: 4, p: 2, border: "1px dashed #ccc", borderRadius: 4 }}>
            <Typography variant="h6" gutterBottom>
              Tambah Produk
            </Typography>
            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
              <FormControl sx={{ minWidth: 220, flexGrow: 1 }}>
                <InputLabel id="select-product-label">Pilih Produk</InputLabel>
                <Select
                  labelId="select-product-label"
                  value={newProductId === "" ? "" : Number(newProductId)}
                  label="Pilih Produk"
                  size="small"
                  onChange={handleNewProductChange}
                >
                  <MenuItem value="">
                    <em>-- Pilih Produk --</em>
                  </MenuItem>
                  {availableProducts.map((p) => (
                    <MenuItem key={p.id} value={Number(p.id)}>
                      {p.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Jumlah"
                type="number"
                value={newQuantityToAdd}
                onChange={handleNewQuantityChange}
                inputProps={{ min: 1, style: { textAlign: "center" } }}
                size="small"
                sx={{ width: 110 }}
              />

              <Button
                variant="contained"
                size="small"
                startIcon={<FaPlus />}
                onClick={handleAddItem}
                disabled={newProductId === "" || newQuantityToAdd < 1}
              >
                Tambah
              </Button>
            </Box>
          </Box>
        )}

        <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button variant="outlined" startIcon={<FaPrint />} onClick={() => setPrintOpen(true)}>
            Cetak
          </Button>

          {canEdit && !isEditing && (
            <Button variant="outlined" startIcon={<FaEdit />} onClick={() => setIsEditing(true)}>
              Edit Pengajuan
            </Button>
          )}

          {isEditing && (
            <>
              <Button variant="contained" color="success" startIcon={<FaSave />} onClick={handleSaveChanges} disabled={saving}>
                {saving ? "Menyimpan…" : "Simpan Perubahan"}
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<FaTimes />}
                onClick={() => {
                  setCurrentItems(detail?.items || []);
                  setIsEditing(false);
                }}
              >
                Batal
              </Button>
            </>
          )}

          {userRole === "admin" && !isEditing && (
            <>
              {uiStatus === "Pending" && (
                <>
                  <Button variant="contained" color="success" startIcon={<FaCheckCircle />} onClick={openApproveModal}>
                    Setujui
                  </Button>
                  <Button variant="contained" color="error" startIcon={<FaTimesCircle />} onClick={openRejectModal}>
                    Tolak
                  </Button>
                </>
              )}
              {(uiStatus === "Approved" || uiStatus === "Rejected") && (
                <Button variant="outlined" color="secondary" startIcon={<FaTrashIcon />} onClick={removeSubmission}>
                  Hapus
                </Button>
              )}
            </>
          )}
        </Box>
      </Paper>

      {/* Dialog PILIH JENIS CETAK */}
      <Dialog open={printOpen} onClose={() => setPrintOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Pilih Jenis Cetak</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Silakan pilih keluaran cetak.
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Button
              variant="contained"
              onClick={() => {
                setPrintOpen(false);
                setTimeout(() => handlePrintSbbk(), 50);
              }}
            >
              Cetak SBBK
            </Button>

            <Button
              variant="outlined"
              onClick={() => {
                setPrintOpen(false);
                setTimeout(() => handlePrintFormPengajuan(), 50);
              }}
            >
              Cetak Form Pengajuan/Permintaan
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrintOpen(false)}>Batal</Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Modal SETUJUI + ALOKASI BATCH UNTUK SEMUA PRODUK */}
      <Dialog open={approveOpen} onClose={() => (busyAction ? null : setApproveOpen(false))} maxWidth="md" fullWidth>
        <DialogTitle>Alokasi Batch (Wajib untuk Semua Produk)</DialogTitle>
        <DialogContent dividers>
          {approveBatchErr && (
            <Alert severity="error" sx={{ mb: 2, whiteSpace: "pre-line" }}>
              {approveBatchErr}
            </Alert>
          )}

          {approveValidateErr && (
            <Alert severity="error" sx={{ mb: 2, whiteSpace: "pre-line" }}>
              {approveValidateErr}
            </Alert>
          )}

          {!wajibBatchItems.length ? (
            <Typography>Tidak ada item (qty &gt; 0). Pengajuan tidak bisa disetujui.</Typography>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Admin wajib membagi <b>Qty Keluar</b> per batch untuk <b>setiap produk</b>. Total qty batch harus <b>sama</b> dengan jumlah pengajuan.
              </Alert>

              {approveLoadingBatches ? (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  {wajibBatchItems.map((it) => {
                    const pid = Number(it.id);
                    const opts = batchOptionsByPid?.[pid] || [];
                    const need = parseInt(it.qty, 10) || 0;
                    const allocated = sumAllocatedForPid(pid);
                    const sisaNeed = need - allocated;

                    return (
                      <Box key={pid} sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                          {it.name} — Butuh: {need}{" "}
                          <span style={{ color: sisaNeed < 0 ? "#d32f2f" : "inherit" }}>(Sisa dialokasikan: {sisaNeed})</span>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Kategori: {it.category}
                        </Typography>

                        <Divider sx={{ my: 1 }} />

                        <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #edf1f5" }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell width={140}>Batch</TableCell>
                                <TableCell width={150}>Kadaluarsa</TableCell>
                                <TableCell width={260}>Sumber</TableCell>
                                <TableCell align="right" width={120}>Stok Sisa</TableCell>
                                <TableCell align="center" width={160}>Qty Keluar</TableCell>
                                <TableCell align="right" width={140}>Sisa Setelah</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {!opts.length ? (
                                <TableRow>
                                  <TableCell colSpan={6} align="center" sx={{ py: 2 }}>
                                    Tidak ada batch tersedia (stok sisa) untuk produk ini.
                                  </TableCell>
                                </TableRow>
                              ) : (
                                opts.map((row) => {
                                  const incomingId = Number(row.id);
                                  const val = batchQtyByPid?.[pid]?.[incomingId] ?? 0;

                                  const stokSisa = Number(row.stok_sisa ?? 0);
                                  const qtyOut = parseInt(val, 10) || 0;
                                  const after = Math.max(0, stokSisa - qtyOut);

                                  const sumber = `${sourceLabel(row.source)}${row.source_note ? ` (${row.source_note})` : ""}`;

                                  return (
                                    <TableRow key={incomingId} hover>
                                      <TableCell>{row.batch || "-"}</TableCell>
                                      <TableCell>{fmtDate(row.tanggal_kadaluarsa)}</TableCell>
                                      <TableCell>{sumber}</TableCell>
                                      <TableCell align="right">{stokSisa}</TableCell>
                                      <TableCell align="center">
                                        <TextField
                                          type="number"
                                          size="small"
                                          value={val === "" ? "" : Number(val)}
                                          onChange={(e) => setBatchQty(pid, incomingId, e.target.value)}
                                          inputProps={{ min: 0, max: stokSisa, step: 1, style: { textAlign: "center" } }}
                                          sx={{ width: 120 }}
                                        />
                                      </TableCell>
                                      <TableCell align="right">{after}</TableCell>
                                    </TableRow>
                                  );
                                })
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>

                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                          * Sistem akan memvalidasi stok batch di server saat disetujui.
                        </Typography>
                      </Box>
                    );
                  })}
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" onClick={() => setApproveOpen(false)} disabled={busyAction}>
            Batal
          </Button>
          <Button variant="contained" color="success" onClick={doApprove} disabled={busyAction || approveLoadingBatches || !wajibBatchItems.length}>
            Ya, Setujui Pengajuan
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal TOLAK */}
      <Dialog open={rejectOpen} onClose={() => (busyAction ? null : setRejectOpen(false))} maxWidth="sm" fullWidth>
        <DialogTitle>Konfirmasi Penolakan</DialogTitle>
        <DialogContent dividers>Anda yakin <b>menolak pengajuan ini</b>?</DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setRejectOpen(false)} disabled={busyAction}>
            Tidak, Lanjutkan Proses Pengajuan
          </Button>
          <Button variant="contained" color="error" onClick={doReject} disabled={busyAction}>
            Ya, Tolak Pengajuan ini
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default SubmissionDetails;
